import { Component, Input, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { isFileTypeBlocked, DigitalItemPublic, FilePresignReturn } from '../../../globalTypes/file.types';
import { OnChanges, SimpleChanges } from '@angular/core';

import { UploadService } from '../services/upload.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { FileTreeNode } from './file-tree';
import { ImagePresignReturn } from '../../../globalTypes/image.types';
import { EventEmitter } from '@angular/core';
import { fileUpload, FileUploadVariables } from '@dataconnect/account';
import { LoadingLogoComponent } from "../component/layout/element/loading-logo.component";

export interface localFile {
  file: File;
  filename: string;
  parentId: string;
  uploading: boolean;
  progress: number;
  uploadedMeta: (FileUploadVariables & {id: string}) | null;
  uploadErrors?: string | null;
}

@Component({
  selector: 'msh-upload-file',
  imports: [MatIconModule],
  template: `
  @if(maxFiles >  totalFiles){
    <div class="flex justify-center w-full">
      <div
        class="border-4 m-auto border-copy/15 cursor-pointer flex w-full items-center justify-center border-dashed rounded  relative"
        (click)="fileInput.click()"
        (dragover)="onDragOver($event, node)"
        (dragleave)="onDragLeave($event, node)"
        (drop)="onDrop($event, node)"
        [class.opacity-50]="!isDragging || uploadingFiles.length"
      >
        <div class="text-center mx-4 text-xs text-copy">
          @if(uploadingFiles.length){
            <div class=" flex ">
              <div class="msh-loading-sm orange !w-8">&nbsp;</div>
              Uploading files...
            </div>
          } @else {
            <mat-icon class="max-h-4 mr-3">cloud_upload</mat-icon>
            Drop or click to upload 
          }
        </div>

        <input
          #fileInput
          type="file"
          class="hidden"
          multiple
          (change)="onFilesSelected($event, node)"
        />
      </div>
    </div>
  }
  `,
  styles: ``,
})
export class UploadFile  implements OnChanges {
/*
tree box
+ folder button
+ file button
+ file dropzone
tree box has dnd with each folder as a container
folders can nest? does it need two containers
load from real or load from memory
delete folder (option to delete child files?)
delete file (if not purchased already... update file?)
folder selection is separate
folder drag and drop is separate (if folder focused move mode action available)
 */
  @Input() node: any = null; // the node that this upload is for (folder or root)
//upload file vars
  @Input() existingFiles: DigitalItemPublic[] = [];
  @Input() locationId!: string;
  @Input() listingId!: string;
  @Input() maxFileSize = '2MB'
  @Input() maxFiles = 10;
  @Output() onFileLoading = new EventEmitter<string[]>();
  @Output() onFilesUpdated = new EventEmitter<DigitalItemPublic[]>();
  maxFileSizeBytes = 2 * 1024 * 1024; // 2 MB
  files: Array<localFile> = []; // local state for previews and upload progress
  uploadingFiles: string[] = [];
  uploadingAny = false;
  hasUploadedTemp = false;
  isDragging = false;
  totalFiles = 0; // total files to upload (existing + new)
  fileObjects: DigitalItemPublic[] = []; // uploaded files that have been registered with the server

  constructor(
    private store: Store,
    private uploadService: UploadService,
    private snack: MatSnackBar,
  ){
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['maxFileSize']) {
      const size = this.maxFileSize.toUpperCase();
      if (size.endsWith('MB')) {
        this.maxFileSizeBytes = parseInt(size.replace('MB', ''), 10) * 1024 * 1024;
      } else if (size.endsWith('KB')) {
        this.maxFileSizeBytes = parseInt(size.replace('KB', ''), 10) * 1024;
      } else if (size.endsWith('GB')) {
        this.maxFileSizeBytes = parseInt(size.replace('GB', ''), 10) * 1024 * 1024 * 1024;
      }
    }
    if(changes['existingFiles']){
      this.totalFiles = this.existingFiles.length + this.files.length;
    }
  }


  dragStarted(node: FileTreeNode): void {
    this.isDragging = true;
  }


  dragEnded(): void {
    this.isDragging = false;
  }

  // Drag handlers
  onDragOver(ev: DragEvent, node: FileTreeNode) {
    ev.preventDefault();
    this.isDragging = true;
  }
  onDragLeave(ev: DragEvent, node: FileTreeNode) {
    this.isDragging = false;
  }

  onDrop(ev: DragEvent, node: FileTreeNode) {
    ev.preventDefault();
      this.isDragging = false;
    const dt = ev.dataTransfer;
    if (dt?.files?.length) {
      this.handleFileList(dt.files, node.id);
    }
  }

  handleFileList(fileList: FileList, parentId: string) {
    const filesArr = Array.from(fileList);
    for (const file of filesArr) {
      if (!this.isValidFile(file)) continue;
      if ([...this.existingFiles, ...this.files].length >= Math.min(this.maxFiles, 10)) {
        this.snack.open(`Max files (${this.maxFiles}) reached`, 'Close', { duration: 2000 });
        break;
      }
      
      const local: any = {
        file,
        filename: file.name,
        contentType: file.type,
        parentId,
        size: file.size,
        progress: 0,
        uploading: false,
        error: null,
        preview: null,
        uploadedMeta: null, // will hold UploadedFile
      };
      this.files.push(local);
      this.triggerUpload();
    }
  }


  onFilesSelected(e: Event, node: FileTreeNode) {
    const input = e.target as HTMLInputElement;
    if (input.files) {
      Array.from(input.files).forEach((file) => {
        this.files.push({ file, filename: file.name, parentId: node.id, uploadedMeta: null, uploading: false, progress: 0 });
      });
    }
    this.triggerUpload();
  }


  isValidFile(file: File) {
    if (isFileTypeBlocked(file)) {
      this.snack.open('Invalid file type', 'Close', { duration: 2000 });
      return false;
    }
    if (file.size > this.maxFileSizeBytes) {
      this.snack.open(`File too large (max ${this.maxFileSize})`, 'Close', { duration: 2000 });
      return false;
    }
    return true;
  }


  
  async triggerUpload() {
    const siblings = this.existingFiles.filter(f => f.parentId === this.node.id);
    let idx = 0;
    for (const f of this.files) {
      if (f.uploadedMeta) continue; // skip already uploaded
      this.updateLoading([f.filename])
      f.uploadErrors = undefined;
      try {
        const presign: FilePresignReturn = await this.uploadService.presignFileUpload({
          locationId: this.locationId,
          listingId: this.listingId,
          filename: f.filename,
          contentType: f.file.type,
        });

        // PUT to R2
        await fetch(presign.uploadUrl, { method: 'PUT', body: f.file, headers: { 'Content-Type': f.file.type } });

        const vars: FileUploadVariables = {
          title: f.filename,
          url: presign.publicUrl,
          filename: f.filename,
          displayOrder: idx,
          contentType: f.file.type,
          sizeInBytes: f.file.size,
          isVideo: f.file.type.startsWith('video/'),
          key: presign.key,
        };
        f.uploadedMeta = { ...vars, id: '' }; // will be updated after server registration

        console.log({vars});
        // await this.uploadService.registerUpload({ key: presign.key, metadata: f.uploadedMeta });
        const uploadResult = await fileUpload(vars);
        
        const id = uploadResult.data.digitalItem_insert.id
        const file: DigitalItemPublic = { id, url: vars.url, statusId: 'initiated', parentId: f.parentId, title: f.filename, displayOrder: idx };
        this.fileObjects.push(file);
        f.uploadedMeta.id = id;
        console.log({id});
        this.hasUploadedTemp = true;
        this.updateLoading([f.filename], true)
        idx++;
      } catch (err) {
        console.log({err: err});
        f.uploadErrors = "Error uploading";
        this.snack.open('Upload failed', 'Close', { duration: 8000 });
      }
    }
    this.totalFiles = this.existingFiles.length + this.files.length;
    this.onFilesUpdated.emit([...this.existingFiles,...this.fileObjects]);
    this.fileObjects = [];
    this.files = [];
  }

  updateLoading(fileNames: string[], remove = false){
    if(remove){
      this.uploadingFiles = this.uploadingFiles.filter(fileName=>!fileNames.includes(fileName))
    }else{
      this.uploadingFiles.push(...fileNames);
    }
    this.onFileLoading.emit(this.uploadingFiles)
  }
}


